/**
 * SeedHistoryTab Component
 *
 * Displays a timeline of seed lifecycle events including creation and status changes.
 * Shows who made changes, when they occurred, and status transitions with visual indicators.
 *
 * @component
 */

import { DashedTag, Seeds as SeedsIcon } from '@/components/chariot-ui';
import { Timeline } from '@/components/Timeline';
import { useMaskingUserType } from '@/hooks/useMaskingUserType';
import { Access, Seed, SeedStatus, SeedStatusLabel } from '@/types';
import { formatDate } from '@/utils/date.util';
import { getAccessEmail } from '@/utils/getAccessEmail';
import { maskPraetorianName } from '@/utils/userDisplay.util';

interface SeedHistoryTabProps {
  /** The seed to display history for */
  seed: Seed;
  /** Access data for resolving user emails */
  accesss: Access[];
}

/**
 * SeedHistoryTab displays seed lifecycle events in a timeline
 *
 * Shows the creation event and all subsequent status changes with:
 * - User who performed the action
 * - Timestamp of the change
 * - Status transition (from -> to) with visual tags
 *
 * @param seed - The seed object containing history and creation metadata
 * @param accesss - Array of access objects for email resolution
 */
export function SeedHistoryTab({ seed, accesss }: SeedHistoryTabProps) {
  const maskingUserType = useMaskingUserType();
  return (
    <Timeline
      items={[
        {
          title: (
            <p>
              <span className="font-bold">Added by</span>{' '}
              {maskPraetorianName(
                getAccessEmail(accesss, seed.origin) || '',
                undefined,
                maskingUserType
              )}
            </p>
          ),
          updated: seed.created,
          description: seed.created && formatDate(seed.created),
          className: 'text-default',
          icon: <SeedsIcon className="" />,
        },
        ...(seed.history
          ?.filter(({ to, updated }) => {
            return !!to && !!updated;
          })
          ?.map(item => {
            const by = getAccessEmail(accesss, item.by);
            const displayBy = by
              ? maskPraetorianName(by, undefined, maskingUserType)
              : by;

            const title = !item.from ? (
              <div>
                {displayBy && <p className="text-sm font-bold">{displayBy}</p>}
                <span>
                  Converted to <span className="text-base font-bold">seed</span>
                </span>
              </div>
            ) : (
              <div>
                {displayBy && <p className="text-sm font-bold">{displayBy}</p>}
                <span>
                  Changed the{' '}
                  <span className="text-base font-bold">status</span> from{' '}
                  <DashedTag
                    size="xs"
                    label={SeedStatusLabel[item.from as SeedStatus]}
                  />{' '}
                  to{' '}
                  <DashedTag
                    size="xs"
                    label={SeedStatusLabel[item.to as SeedStatus]}
                  />
                </span>
              </div>
            );

            return {
              title,
              updated: item.updated,
              description: formatDate(item.updated),
              className: 'text-default',
            };
          }) || []),
      ].reverse()}
    />
  );
}
